// MaturaSquarePyramidBaseEdgeClosedTask.js
// Zadanie maturalne – zamknięte (1 pkt)
// Ostrosłup prawidłowy czworokątny.

import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import ABCDInput from '../../../../components/inputs/ABCDInput';

// ================== UTILSY ==================

const rndInt = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// uproszczenie pierwiastka: n -> (np.) "5\\sqrt{5}", "10", "\\sqrt{6}"
function simplifySqrtLatex(n) {
  if (!Number.isFinite(n) || n <= 0) {
    return `\\sqrt{${n}}`;
  }
  let rest = Math.round(n);
  let outside = 1;

  for (let i = 2; i * i <= rest; i++) {
    while (rest % (i * i) === 0) {
      outside *= i;
      rest /= i * i;
    }
  }

  if (rest === 1) {
    // idealny kwadrat
    return `${outside}`;
  }

  if (outside === 1) {
    return `\\sqrt{${rest}}`;
  }

  return `${outside}\\sqrt{${rest}}`;
}

function makeSqrtOfFractionLatex(numerator, denominator) {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
    return `\\sqrt{${numerator}/${denominator}}`;
  }

  const num = Math.round(numerator);
  const den = Math.round(denominator);

  if (den === 1) {
    // zwykły pierwiastek z liczby całkowitej
    return simplifySqrtLatex(num);
  }

  if (num % den === 0) {
    // da się skrócić do liczby całkowitej
    const inside = num / den;
    return simplifySqrtLatex(inside);
  }

  // zostawiamy w postaci pierwiastka z ułamka
  return `\\sqrt{\\dfrac{${num}}{${den}}}`;
}

function makeFractionLatex(numerator, denominator) {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
    return `${numerator}`;
  }

  const num = Math.round(numerator);
  const den = Math.round(denominator);

  if (den === 1) {
    return `${num}`;
  }

  if (num % den === 0) {
    return `${num / den}`;
  }

  return `\\dfrac{${num}}{${den}}`;
}


// ================== GENERATOR ==================

function generateJune2025Original() {
  return {
    type: 'MaturaSquarePyramidBaseEdgeClosedTask',
    data: {
      a: 2,
      k: 4,
      totalArea: 20,
      baseArea: 4,
      options: [
        { label: 'A', latex: '2', isCorrect: true },
        { label: 'B', latex: '\\sqrt{5}', isCorrect: false },
        { label: 'C', latex: '\\sqrt{10}', isCorrect: false },
        { label: 'D', latex: '4', isCorrect: false },
      ],
      correctIndex: 0,
    },
    source: {
      variant: 'original',
      exam: 'MATURA',
      session: '2025_CZE',
      taskNumber: 25,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === 'original' &&
    generationOptions.session === '2025_CZE'
  ) {
    return generateJune2025Original();
  }

  // długość krawędzi podstawy – unikamy 1, żeby nie było banalnie
  const a = rndInt(2, 5); // 2,3,4,5

  // ile razy większe jest pole boczne od pola podstawy
  const k = rndInt(2, 5); // 2–5 razy

  // P_p = a^2
  const baseArea = a * a;

  // P_c = (1 + k) · a^2
  const totalArea = (1 + k) * baseArea;

  // poprawna odpowiedź: a
  const correctLatex = `${a}`;

  // ───── pula dystraktorów ─────
  const distractorsPool = [];

  const addDistractor = (latex) => {
    if (!latex) return;
    if (latex === correctLatex) return;
    if (distractorsPool.find((d) => d.latex === latex)) return;
    distractorsPool.push({ latex });
  };

  // (1) a = sqrt(P_c) – zapomniano o dzieleniu przez (1+k)
  addDistractor(makeSqrtOfFractionLatex(totalArea, 1));

  // (2) a = sqrt(P_c / k) – użyto k zamiast (1+k)
  addDistractor(makeSqrtOfFractionLatex(totalArea, k));

  // (3) a = sqrt(P_c / 2) – potraktowano "dwie części": podstawa i boczna
  addDistractor(makeSqrtOfFractionLatex(totalArea, 2));

  // (4) a = P_p = S / (1+k) – pomylenie pola podstawy z bokiem / brak pierwiastka
  addDistractor(makeFractionLatex(totalArea, 1 + k));

  // (5) prosta "intuicyjna" liczba całkowita obok poprawnej, np. a+1
  addDistractor(`${a + 1}`);

  // losujemy 3 różne destraktory z dostępnej puli
  const shuffledPool = shuffle(distractorsPool);
  const pickedDistractors = shuffledPool.slice(0, 3);

  // składamy pełną listę opcji
  const rawOptions = [
    { latex: correctLatex, isCorrect: true },
    ...pickedDistractors.map((d) => ({ ...d, isCorrect: false })),
  ];

  const shuffled = shuffle(rawOptions).map((opt, idx) => ({
    label: String.fromCharCode(65 + idx), // A, B, C, D
    ...opt,
  }));


  const correctIndex = shuffled.findIndex((opt) => opt.isCorrect);

  return {
    type: 'MaturaSquarePyramidBaseEdgeClosedTask',
    data: {
      a,
      k,
      totalArea,
      baseArea,
      options: shuffled,
      correctIndex,
    },
  };
}

// ================== TREŚĆ ZADANIA ==================

export function renderTask({ data }) {
  const { k, totalArea, options } = data;

  const PcLatex = `${totalArea}`;

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Zadanie maturalne – zamknięte (0–1 pkt).</strong>
      </p>

      <p>
        Pole powierzchni całkowitej ostrosłupa prawidłowego czworokątnego jest
        równe <InlineMath math={PcLatex} />.
      </p>

      <p>
        Pole powierzchni bocznej tego ostrosłupa jest{' '}
        <InlineMath math={String(k)} /> razy większe od pola jego podstawy.
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
        <br />
        Długość krawędzi podstawy tego ostrosłupa jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ================== INPUT (ABCD) ==================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === 'number'
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ================== CHECK ==================

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== 'number') {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

// ================== POPRAWNA ODPOWIEDŹ ==================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

// ================== ODPOWIEDŹ UŻYTKOWNIKA ==================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== 'number' ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ================== WYJAŚNIENIE ==================

export function renderExplanation({ data }) {
  const { k, totalArea, baseArea, a } = data;

  const onePlusK = 1 + k;

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Krok 1.</strong> Oznaczmy pole podstawy ostrosłupa przez{' '}
        <InlineMath math={'P_p'} /> oraz pole powierzchni bocznej przez{' '}
        <InlineMath math={'P_b'} />. Pole powierzchni całkowitej spełnia
        zależność:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={'P_c = P_p + P_b'} />
      </div>

      <p>
        Z treści zadania wiemy, że pole powierzchni bocznej jest{' '}
        <InlineMath math={String(k)} /> razy większe od pola podstawy, czyli:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`P_b = ${k}P_p.`} />
      </div>

      <p>Podstawiamy to do wzoru na pole całkowite:</p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`P_c = P_p + ${k}P_p = ${onePlusK}P_p.`} />
      </div>

      <p>
        <strong>Krok 2.</strong> Dla naszego ostrosłupa prawidłowego
        czworokątnego pole całkowite jest równe{' '}
        <InlineMath math={`P_c = ${totalArea}`} />. Zapisujemy równanie:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`${totalArea} = ${onePlusK}P_p`} />
      </div>

      <p>Stąd obliczamy pole podstawy:</p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath
          math={`P_p = \\dfrac{${totalArea}}{${onePlusK}} = ${baseArea}.`}
        />
      </div>

      <p>
        <strong>Krok 3.</strong> Podstawa ostrosłupa jest kwadratem o boku{' '}
        <InlineMath math={'a'} />, więc:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={'P_p = a^{2}'} />
      </div>

      <p>Podstawiamy obliczone pole podstawy. Ponieważ <InlineMath math="a>0" />,
        wybieramy dodatni pierwiastek:</p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`a^{2} = ${baseArea}`} />
        <BlockMath math={`a = \\sqrt{${baseArea}} = ${a}.`} />
      </div>

      <p>
        Zatem długość krawędzi podstawy tego ostrosłupa jest równa{' '}
        <InlineMath math={`${a}`} />.
      </p>
    </div>
  );
}
